import classNames from 'classnames';
import { CourseBadgeFamily, SubsDiff } from '@udemy-v2/react-plans-differentiation';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, SubscribeFromPageCookie, SubscriptionOfferImpressionEvent, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { tokens } from '@udemy-v2/styles';
import { TrackImpression, publishEvent } from '@udemy-v2/event-tracking';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import { ClickEvent, CourseIncentiveImpressionEvent, IncentiveType, LearningProduct } from '../../events/events';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { useClpPurchaseContext } from '../../context/clp-purchase-context.react-component';
import { useClpSubscriptionContext } from '../../context/clp-subscription-context.react-component';
import { useClpContext } from '../../context/clp-context-provider.react-component';
import styles from './use-subs-diff.module.scss';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
const PPLP_URL = 'https://udemy.com/personal-plan/';
const SUBS_DIFF_PPLP_LINK_COMPONENT_NAME = 'subs_diff_pplp_link';
export interface SubsDiffResult {
  shouldAddSubsDiffSpacing: boolean;
  shouldShowSubsDiff: boolean;
  isSubsDiffLoading: boolean;
  SubsDiffComponent: React.FC;
}
export const useSubsDiff = (): SubsDiffResult => {
  const isDesktop = useMatchMedia(`(min-width: ${tokens['viewport-lg-min']})`);
  const course = useClpCourse();
  const hasOrganization = useUserHasOrganization();
  const {
    isCourseInAvailablePlans,
    isCourseIncludedInUserSubscription,
    isPersonalPlanSubscriber,
    unwrappedConsumerSubscriptionPlan,
    subscriptionTiersEnabled,
    clpGenericUdemyPlansCopyEnabled,
    isFeaturesLoading
  } = useClpPurchaseContext();
  const {
    isCourseInSelectPlansOnly,
    isCourseInValuePlanCatalog,
    subscribedProductType,
    isSubscriptionPlanLoading,
    unwrappedValuePlan
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(contentCollection => contentCollection.stats)) : FALLBACK_COLLECTION_STATS;
  const valuePlanContentCollections = unwrappedValuePlan?.contentCollections;
  const valuePlanStats = valuePlanContentCollections ? aggregateCollectionStats(valuePlanContentCollections.map(contentCollection_0 => contentCollection_0.stats)) : undefined;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const handlePplpLinkClick = () => {
    SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
    publishEvent(new ClickEvent({
      componentName: SUBS_DIFF_PPLP_LINK_COMPONENT_NAME,
      trackingId: course.id,
      relatedObjectType: LearningProduct.COURSE,
      relatedObjectId: parseInt(course.id, 10)
    }));
  };
  const handleBadgePopoverView = () => {
    if (unwrappedConsumerSubscriptionPlan) {
      publishEvent(new SubscriptionOfferImpressionEvent({
        badges: null,
        courseId: Number(course.id),
        courseTrackingId: String(course.id),
        hasTrial: Boolean(unwrappedConsumerSubscriptionPlan.trialPeriodDays),
        planId: unwrappedConsumerSubscriptionPlan.id,
        planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
        trialDays: unwrappedConsumerSubscriptionPlan.trialPeriodDays,
        uiRegion: 'body'
      }));
    }
  };
  const badgeFamily: CourseBadgeFamily = (() => {
    // resolves badge to render
    if (subscriptionTiersEnabled) {
      if (isCourseInValuePlanCatalog) return CourseBadgeFamily.STARTER_PLAN;
      if (isCourseInSelectPlansOnly) return CourseBadgeFamily.PERSONAL_PLAN;
    }
    return CourseBadgeFamily.PREMIUM;
  })();

  // check if this course in a plan we can upsell
  const isCourseEligible = subscriptionTiersEnabled ? isCourseInValuePlanCatalog || isCourseInSelectPlansOnly : isCourseInAvailablePlans;

  // check if this user someone we'd upsell to
  const isEligibleForUpsell = !hasOrganization && !isPersonalPlanSubscriber && !isCourseIncludedInUserSubscription;

  // free courses have nothing to upsell, so they never get the plan badge
  const shouldShowSubsDiff = !isFeaturesLoading && !course.isFree && isCourseEligible && isEligibleForUpsell;
  const {
    numCourses: resolvedNumCourses,
    isLoadingStats: isLoadingCollectionStats
  } = (() => {
    if (badgeFamily === CourseBadgeFamily.STARTER_PLAN) {
      if (clpGenericUdemyPlansCopyEnabled) {
        if (personalPlanStats && 'numberOfCourses' in personalPlanStats) {
          return {
            numCourses: personalPlanStats.numberOfCourses,
            isLoadingStats: false
          };
        }
        if (personalPlanStats && 'isLoading' in personalPlanStats && personalPlanStats.isLoading) {
          return {
            numCourses: undefined,
            isLoadingStats: true
          };
        }
        return {
          numCourses: undefined,
          isLoadingStats: false
        };
      }
      if (valuePlanStats) {
        return {
          numCourses: valuePlanStats.numberOfCourses,
          isLoadingStats: false
        };
      }
      return {
        numCourses: undefined,
        isLoadingStats: true
      };
    }
    if (badgeFamily === CourseBadgeFamily.PERSONAL_PLAN) {
      if (isGwgStandaloneSubscriber) {
        return {
          numCourses: subscriptionPlanStats.numberOfCourses,
          isLoadingStats: isSubscriptionPlanLoading
        };
      }
      if (personalPlanStats && 'numberOfCourses' in personalPlanStats) {
        return {
          numCourses: personalPlanStats.numberOfCourses,
          isLoadingStats: false
        };
      }
      if (personalPlanStats && 'isLoading' in personalPlanStats && personalPlanStats.isLoading) {
        return {
          numCourses: undefined,
          isLoadingStats: true
        };
      }
      return {
        numCourses: undefined,
        isLoadingStats: false
      };
    }
    return {
      numCourses: subscriptionPlanStats.numberOfCourses,
      isLoadingStats: false
    };
  })();
  const SubsDiffComponent: React.FC = () => {
    const {
      openReviewsModal
    } = useClpContext();
    if (!shouldShowSubsDiff) return null;
    return <div className={classNames('ud-color-scheme-light', styles['subs-diff-wrapper'])}>
                <TrackImpression trackFunc={() => publishEvent(new CourseIncentiveImpressionEvent({
        courseId: Number(course.id),
        courseTrackingId: String(course.id),
        incentiveType: IncentiveType.SUBSCRIPTION_DIFFERENTIATION,
        incentiveValue: '',
        position: 0
      }))}>
                    <div className={styles['subs-diff-container']}>
                        <SubsDiff badgeFamily={badgeFamily} numLearners={course.enrollments?.count || 0} numRatings={course.rating?.count || 0} rating={course.rating?.average || 0} isLoadingCollectionStats={isLoadingCollectionStats} numCourses={resolvedNumCourses} onRatingsClick={openReviewsModal} onBadgePopoverView={handleBadgePopoverView} useGenericPlansCopy={clpGenericUdemyPlansCopyEnabled} pplpUrl={PPLP_URL} onPplpLinkClick={handlePplpLinkClick} />
                    </div>
                </TrackImpression>
            </div>;
  };
  return {
    shouldAddSubsDiffSpacing: Boolean(isDesktop) && shouldShowSubsDiff,
    shouldShowSubsDiff,
    isSubsDiffLoading: isFeaturesLoading || !!unwrappedConsumerSubscriptionPlan?.isLoading,
    SubsDiffComponent
  };
};